import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

// Components 
import Box from '@mui/material/Box';
import Sidebar from './sidebar/Sidebar';
import { resetConsultationNote, setConsultationNoteId, setIsSecurityPopupOpened, setIsSidebarOpened, setMode, setSecurityPin, DefaultPatientInfoLabels } from '../../redux/features/doctor/Dashboard/consultation/consultationNote';
import Document from './document/Document';
import Navbar from './navbar/Navbar';
import ExitPopup from './navbar/items/ExitPopup';
import AccessPopup from './document/AccessPopup';
import ExistingFieldPopup from './dropdown/fields/ExistingFieldPopup';
import AttributesDropdown from './dropdown/attributes/AttributesDrowpdown';
import FieldsDropdown from './dropdown/fields/FieldsDropdown';
import { getConsultation } from '../../routes/doctor/consultation';
import { setClosePopup, setOpenPopup, setPopupAction } from '../../redux/features/general/popup';
import { setNotificationMessage } from '../../redux/features/general/notification';

// -- 

const Container: React.FC = () => {
    const dispatch = useAppDispatch(); 
    const { t } = useTranslation(); 
    const navigate = useNavigate(); 
    const location = useLocation(); 

    const consultationNoteContainerRef = useRef() as any;

    const { consultationId, consultationNoteId } = useParams() as any; 

    const isPopupOpened = useAppSelector((state) => state.popup.isPopupOpened); 
    const isSavingEnabled = useAppSelector(state => state.consultationNote.isSavingEnabled); 
    const securityPin = useAppSelector(state => state.consultationNote.securityPin); 
    const popupAction = useAppSelector(state => state.popup.action); 
    const action = useAppSelector(state => state.consultationNote.action); 
    const isSecurityPopupOpened = useAppSelector(state => state.consultationNote.isSecurityPopupOpened); 
    const isSidebarOpened = useAppSelector((state) => state.consultationNote.isSidebarOpened);
    const [timeLeft, setTimeLeft] = useState(2); 

    // -- 

    window.onbeforeunload = function (e: any) {
      if (isSavingEnabled) { 
        if (e) {
          e.returnValue;
        }; 
        return e.returnValue;
      };
    };

    // --

    consultationNoteContainerRef?.current?.addEventListener("keydown", (e: any) => {
      if ((e.key === 's') && (e.ctrlKey) && (e.altKey)) { 
          dispatch(setMode('saveConsultationNote')); 
          return; 
      } else if ((e.key === 'b') && (e.ctrlKey) && (e.altKey)) { 
          dispatch(setIsSidebarOpened(!isSidebarOpened)); 
          return; 
      };
    });

    // -- 

    const verifyConsultation = async () => { 
      const res = await getConsultation(consultationId) as any; 
      const translatedLabels: DefaultPatientInfoLabels = {
          name: t('pdf.label.name', 'Name:'),
          dob: t('pdf.label.dob', 'DOB:'),
          healthCard: t('pdf.label.healthCard', 'Health Card:'),
      };

      if (res.data.status === 200) { 
            if (res.data.consultation.status !== 'pending') { 
                dispatch(resetConsultationNote({ labels: translatedLabels })); 
                navigate('/dashboard'); 
            }; 

            if (res.data.consultation.securityPin !== securityPin) { 
                dispatch(setSecurityPin(res.data.consultation.securityPin)); 
            }; 

            if (res.data.isAccessRequested) { 
                if (!isSecurityPopupOpened) { 
                    dispatch(setPopupAction('securityPin')); 
                    dispatch(setOpenPopup()); 
                    dispatch(setIsSecurityPopupOpened(true)); 
                }; 
            }; 

            if (res.data.isDeviceConnected) { 
              dispatch(setNotificationMessage(t('deviceConnected'))); 
              if (isPopupOpened && (popupAction === 'securityPin')) { 
                dispatch(setClosePopup()); 
              }; 
            }; 
            
      } else { 
        dispatch(resetConsultationNote({ labels: translatedLabels })); 
        navigate('/dashboard'); 
      }; 
    }; 

    // -- 

    useEffect(() => { 
        if (consultationNoteId) { 
            const translatedLabels: DefaultPatientInfoLabels = {
                name: t('pdf.label.name', 'Name:'),
                dob: t('pdf.label.dob', 'DOB:'),
                healthCard: t('pdf.label.healthCard', 'Health Card:'),
            };
            dispatch(setConsultationNoteId({ id: consultationNoteId, labels: translatedLabels })); 
        }; 
    },[consultationNoteId, t, dispatch]); 

    // -- 
  
    useEffect(()=>{
        let myInterval = setInterval(() => {
            if (timeLeft > 0) {
                setTimeLeft(timeLeft - 1);
            }; 
            if (timeLeft === 1) { 
              if (consultationId && (!location?.pathname.includes('template'))) { 
                verifyConsultation(); 
              }; 
              setTimeLeft(2); 
            }; 
            if (timeLeft === 0) {
                if (timeLeft === 0) {
                    clearInterval(myInterval); 
                };
            }; 
        }, 500); 
        return ()=> {
            clearInterval(myInterval);
        };
    },[timeLeft]);

    // -- 

    return (
      <Box
        ref={consultationNoteContainerRef}
        sx={{ 
          width: '100%', 
          height: '100%', 
        }}
      >
        <Sidebar /> 
        <Document /> 
        <Navbar /> 
        {(action === 'attribute') && <AttributesDropdown />}
        {(action === 'newNoteField') && <FieldsDropdown />}
      </Box>
  );
};

export default Container;